import { useMemo } from 'react'

import { clsx } from 'clsx'

import {
  PendingMessage,
  useCachedParticipants,
} from '@tribeplatform/react-sdk/hooks'
import {
  Chat,
  ChatStatus,
  ChatType,
  Message,
} from '@tribeplatform/react-sdk/types'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { T } from '../../../i18n/index.js'
import { useChatContext } from '../../ChatContext/ChatContext.js'
import { isChatToMyself } from '../utils/chat.utils.js'

interface Props {
  message: Message | PendingMessage
  isMe: boolean
  chat: Chat
}

export const ChatMessageAuthorName = ({ message, isMe, chat }: Props) => {
  const { data: participants } = useCachedParticipants({ chatId: chat._id })
  const isTombstone = chat.status === ChatStatus.Tombstone
  const participant = useMemo(() => {
    if (chat.type === ChatType.Direct) {
      return isChatToMyself(chat)
        ? chat.currentParticipant
        : chat.otherParticipant
    }
    return participants[message.authorParticipantId]
  }, [participants, chat, message.authorParticipantId])
  const name = useMemo(() => {
    if (isMe) {
      return <T id="Generics.You" defaultMessage="You" />
    }
    if (isTombstone) {
      return message.authorName
    }
    return participant?.name
  }, [isMe, isTombstone, message.authorName, participant?.name])
  const { toggleShowChatDetails } = useChatContext()
  const showDetailsOnClick = chat.type === ChatType.Group || !isMe

  return (
    <div
      className={clsx(
        'font-medium text-sm text-content truncate cursor-pointer flex items-center gap-x-1 leading-6',
      )}
      translate="no"
    >
      {!name && (
        <div className="bg-skeleton rounded-base animate-pulse">
          {/* To have exact height of the actual text and avoid content shift */}
          {/* eslint-disable-next-line react/jsx-no-literals */}
          <span className="invisible">Name placeholder</span>
        </div>
      )}
      <Text
        className={clsx(
          showDetailsOnClick &&
            'hover:text-content-subdued transition-all duration-200',
        )}
        onClick={() =>
          showDetailsOnClick ? toggleShowChatDetails(participant) : undefined
        }
      >
        {name}
      </Text>
    </div>
  )
}
